export type KioskStepKey =
  | "welcome"
  | "lookup"
  | "pick"
  | "info"
  | "review";

export interface KioskStepDef {
  key: KioskStepKey;
  label: string;
}

export interface ComputeKioskStepsInput {
  speakersCanRegister: boolean | null | undefined;
  openAgendaItemCount: number;
  /** Captured at hydration, never derived from the ambient selections map (AB#9876). */
  alreadyHoldsSlots: boolean;
  returningUser: boolean;
  /** The resident is on Find your registration, heading for Review. */
  lookingUp: boolean;
}

const WELCOME: KioskStepDef = { key: "welcome", label: "Welcome" };
const LOOKUP: KioskStepDef = { key: "lookup", label: "Find your registration" };
const PICK: KioskStepDef = { key: "pick", label: "Pick items" };
const INFO: KioskStepDef = { key: "info", label: "Your info" };
const REVIEW: KioskStepDef = { key: "review", label: "Review" };

export function shouldShowPickStep(input: ComputeKioskStepsInput): boolean {
  return (
    (input.speakersCanRegister === true || input.alreadyHoldsSlots) &&
    input.openAgendaItemCount > 0
  );
}

export function shouldShowReviewStep(input: ComputeKioskStepsInput): boolean {
  return shouldShowPickStep(input) || input.returningUser || input.lookingUp;
}

/**
 * Welcome -> [Find your registration] -> [Pick items] -> Your info -> [Review].
 *
 * Find your registration replaces the entry steps rather than sitting alongside them: a resident
 * on that path is identifying an existing registration, not filling one in.
 */
export function computeKioskSteps(
  input: ComputeKioskStepsInput
): KioskStepDef[] {
  if (input.lookingUp) return [WELCOME, LOOKUP, REVIEW];

  const steps: KioskStepDef[] = [WELCOME];
  if (shouldShowPickStep(input)) steps.push(PICK);
  steps.push(INFO);
  if (shouldShowReviewStep(input)) steps.push(REVIEW);
  return steps;
}
